<template>
  <div>
    <table
      class="table is-striped is-fullwidth is-borderless is-spaced-top"
    >
      <thead class="has-background-info">
        <tr>
          <th class="has-text-white is-uppercase">
            type
          </th>
          <th class="has-text-white is-uppercase">
            account
          </th>
          <th class="has-text-white is-uppercase">
            contacts
          </th>
          <th class="has-text-white is-uppercase">
            actions
          </th>
        </tr>
      </thead>

      <tbody>
        <tr>
          <td>
            <b-field>
              <b-select
                v-model="payload.influencerTypeId"
                expanded
              >
                <option
                  v-for="(influencerType, index) in influencerTypes"
                  :key="index"
                  :value="influencerType.value"
                >
                  {{ influencerType.value }}
                </option>
              </b-select>
            </b-field>
          </td>

          <td>
            <b-field>
              <b-autocomplete
                v-model="payload.account"
                expanded
                field="name"
                icon-right="search"
                :data="accountsSearchResults"
                :loading="isLoading"
                @typing="getAccountsAsyncData"
                @select="(option) => (selectedAccount = option)"
              >
                <template slot-scope="props">
                  <div class="media">
                    <div class="media-content">
                      {{ props.option.name }}
                      <br>
                      <small>
                        customer number: {{ props.option.externalId }}
                      </small>
                    </div>
                  </div>
                </template>

                <template #empty>
                  No results for {{ payload.account }}
                </template>
              </b-autocomplete>
            </b-field>
          </td>

          <td>
            <b-field>
              <b-autocomplete
                v-model="contact"
                :data="contactsSearchResults"
                field="contactName"
                clear-on-select
                :loading="isLoadingContacts"
                :open-on-focus="true"
                @select="addContact"
                @typing="getContactsAsyncData"
              >
                <template slot-scope="props">
                  <div class="media">
                    <div class="media-left">
                      <img
                        width="32"
                        :src="props?.option?.photo ? props.option.photo : defaultImage"
                        :alt="props?.option?.contactName"
                      >
                    </div>
                    <div class="media-content">
                      {{ props?.option?.contactName?.trim() }}
                      <br>
                      <small>
                        Phone: {{ props?.option?.phone }}, Email:
                        <b>{{ props?.option?.email }}</b>
                      </small>
                    </div>
                  </div>
                </template>

                <template #empty>
                  <template v-if="isLoadingContacts">
                    Loading contacts...
                  </template>

                  <template v-else>
                    No results for {{ contact }}
                  </template>
                </template>
              </b-autocomplete>
            </b-field>

            <a
              v-if="selectedAccount"
              href="#"
              class="link"
              style="display: block;"
              @click="openModal('newContactFormModal')"
            >
              <strong>Create New Contact</strong>
            </a>

            <b-taglist>
              <b-tag
                v-for="(item, index) in payload.contacts"
                :key="index"
                closable
                @close="removeContact(index)"
              >
                {{ item.contact }}
              </b-tag>
            </b-taglist>
          </td>

          <td>
            <b-button
              icon-left="plus"
              type="is-success"
              aria-label="Add"
              @click="addInfluencer"
            >
              Add Influencer
            </b-button>
          </td>
        </tr>

        <tr
          v-for="(influencer, index) in influencers"
          :key="influencer.id || `new-${index}`"
        >
          <td>
            <span>
              {{ influencer.influencerTypeId }}
            </span>
          </td>

          <td>
            {{ influencer.account }}
          </td>

          <td>
            <b-tag
              v-for="(influencerContact, id) in influencer.contacts"
              :key="id"
            >
              {{ influencerContact.contact }}
            </b-tag>
          </td>

          <td>
            <b-button
              aria-label="Remove Influencer"
              icon-left="close"
              type="is-danger"
              @click="
                $route.name === 'EditOpportunity'
                  ? removeInfluencer(index, influencer.accountId)
                  : removeInfluencer(index, influencer.accountId)
              "
            />
          </td>
        </tr>
      </tbody>
    </table>

    <b-modal
      v-model="isComponentModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      aria-role="dialog"
      aria-label="create data form modal"
      aria-modal
    >
      <template #default="props">
        <accounts-form-modal
          v-if="modalName === 'newAccountFormModal'"
          module-name="opportunities"
          @close="props.close"
        />
        <contacts-form-modal
          v-if="modalName === 'newContactFormModal'"
          module-name="opportunities"
          :influencer-account-id="payload.accountId"
          @close="props.close"
          @set-contact="setContact"
        />
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import debounce from '@/utils/debounce';

import AccountsFormModal from '@/components/Shared/AccountsFormModal.vue';
import ContactsFormModal from '@/components/Shared/ContactsFormModal.vue';

import defaultImage from '@/assets/images/avatar.svg';

export default {
  name: 'OpportunitiesInfluencersTable',

  components: {
    AccountsFormModal,
    ContactsFormModal,
  },

  data() {
    return {
      defaultImage,
      isLoading: false,
      isLoadingContacts: false,
      selectedAccount: null,
      accountsSearchResults: [],
      isInfluencerContactFormModalActive: false,
      payload: {
        influencerTypeId: '',
        account: '',
        accountId: '',
        contacts: [],
      },
      selectedContact: null,
      contactsSearchResults: [],
      contact: '',
      isComponentModalActive: false,
      modalName: '',
    };
  },

  computed: {
    ...mapGetters({
      influencers: 'Opportunities/getOpportunityInfluencers',
      influencerTypes: 'Lookups/getAccountTypes',
    }),
  },

  watch: {
    async selectedAccount(value) {
      if (value.id) {
        this.payload.accountId = value.id;
        this.contactsSearchResults = [];
        this.isLoadingContacts = true;
        const response = await this.$store.dispatch('Contacts/fetchAccountContactList', {
          page: 0,
          size: 1000000,
          sort: ['updatedAt', 'desc'],
          filters: `&${new URLSearchParams({ accountIds: value.id }).toString()}`,
        });
        this.contactsSearchResults = response;
        this.isLoadingContacts = false;
      }
    },

    account() {
      this.contactsSearchResults = [];
    },
  },

  methods: {
    openModal(modalLabel) {
      this.modalName = modalLabel;
      this.isComponentModalActive = true;
    },

    setContact(payload) {
      console.log('setContact - payload', payload);
      this.payload.contacts.push({
        contact: `${payload.firstName} ${payload.lastName}`,
        contactId: payload.id,
      });
    },

    getAccountsAsyncData: debounce(async function (token) {
      this.isLoading = true;
      try {
        const response = await this.$store.dispatch('Accounts/searchTypeAccounts', {
          page: 0,
          size: 10,
          sort: ['updatedAt', 'desc'],
          filters: `&${new URLSearchParams({ accountType: this.payload.influencerTypeId, name: token }).toString()}`,
        });
        this.accountsSearchResults = response;
      } catch (error) {
        this.accountsSearchResults = [];
      } finally {
        this.isLoading = false;
      }
    }, 500),

    getContactsAsyncData: debounce(async function (token) {
      try {
        const response = await this.$store.dispatch('Contacts/fetchAccountContactList', {
          page: 0,
          size: 10,
          sort: ['updatedAt', 'desc'],
          filters: `&${new URLSearchParams({ accountIds: this.payload.accountId, contactName: token }).toString()}`,
        });
        this.contactsSearchResults = response;
      } catch (error) {
        this.contactsSearchResults = [];
      }
    }, 500),

    async refreshInfluencersFromGrid() {
      const influencersRes = await this.$store.dispatch('DataGrids/fetchInfluencers', {
        opportunityId: this.$route.params.opportunityId,
        page: 0,
        size: 100,
        sort: ['updatedAt', 'desc'],
        filters: '',
      });
      this.$store.commit('Opportunities/SET_OPPORTUNITY_INFLUENCERS', influencersRes);
    },

    async addInfluencer() {
      const nonExistentContacts = [];
      this.influencers.forEach((influencer) => {
        if (influencer.accountId === this.payload.accountId) {
          this.payload.contacts.forEach((contact) => {
            const isExisting = influencer.contacts.some(
              (influencerContact) => influencerContact.contactId === contact.contactId,
            );
            if (!isExisting) {
              nonExistentContacts.push(contact);
            }
          });
        }
      });

      // check if account already exists
      const isAccountExistent = this.influencers.find(
        (influencer) => influencer.accountId === this.payload.accountId,
      );

      if (isAccountExistent && nonExistentContacts.length > 0) {
        if (this.$route.name === 'EditOpportunity') {
          try {
            await this.$store.dispatch('Opportunities/createOpportunityInfluencer', {
              opportunityId: this.$route.params.opportunityId,
              influencer: this.payload,
            });
            await this.refreshInfluencersFromGrid();
          } catch (error) {
            console.error(error);
          }
        } else {
          this.$store.commit('Opportunities/UPDATE_OPPORTUNITY_INFLUENCER', {
            accountId: this.payload.accountId,
            contacts: nonExistentContacts,
          });
        }
      } else {
        if (this.$route.name === 'AddOpportunity') {
          this.$store.commit('Opportunities/ADD_OPPORTUNITY_INFLUENCER', { ...this.payload });
        }

        if (this.$route.name === 'EditOpportunity') {
          try {
            await this.$store.dispatch('Opportunities/createOpportunityInfluencer', {
              opportunityId: this.$route.params.opportunityId,
              influencer: this.payload,
            });
            await this.refreshInfluencersFromGrid();
          } catch (error) {
            console.error(error);
          }
        }
      }

      this.payload = {
        influencerTypeId: '',
        account: '',
        accountId: '',
        contacts: [],
      };

      this.contact = '';
    },

    async removeInfluencer(index, accountId) {
      if (this.$route.name === 'AddOpportunity') {
        this.$store.commit('Opportunities/REMOVE_OPPORTUNITY_INFLUENCER', index);
        return;
      }

      if (this.$route.name === 'EditOpportunity') {
        try {
          await this.$store.dispatch('Opportunities/removeOpportunityInfluencer', {
            opportunityId: this.$route.params.opportunityId,
            influencerId: index,
            accountId,
          });
          await this.refreshInfluencersFromGrid();
        } catch (error) {
          console.error(error);
        }
      }
    },

    addContact(option) {
      const isExistingContact = this.payload.contacts.find(
        (contact) => contact.contactId === option.id,
      );
      if (!isExistingContact) {
        this.payload.contacts.push({ contact: option.contactName, contactId: option.id });
      }
      this.selectedContact = null;
      this.contact = '';
      this.contactsSearchResults = [];
    },

    removeContact(index) {
      this.payload.contacts.splice(index, 1);
    },
  },
};
</script>

<style lang="css" scoped></style>
